// ─── Écran REPAS : compositions (recettes + extras) ────────────────
// `onFermer` optionnel (18/08) : dans My Meals (onglet dédié) l'écran vit au
// niveau racine ; dans My Calendar (hub Famille) il est posé en calque et a
// besoin du geste + de la flèche de retour — même correctif que EcranCourses.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran,
  useUI, useT, useAuth, useEspace, confirmerSuppression,
} from '@mop/core';
import { ecouterRecettes } from './services/recettes';
import {
  ecouterRepasComposes, creerRepas, modifierRepas, supprimerRepas, dupliquerRepas,
  coutRepas, resumeRepas,
} from './services/repasComposes';
import { ingredientsRepasEchelle } from './services/courses';
import { personnesRepas } from './services/personnes';
import { ecouterListeCourses, enregistrerListeCourses, fusionnerDansListe } from './services/listeCourses';
import useCatalogueIngredients from './hooks/useCatalogueIngredients';
import FeuilleRepas from './FeuilleRepas';
import FicheRepas from './FicheRepas';
import FeuilleAjoutCourses from './FeuilleAjoutCourses';

export default function EcranRepas({ onFermer } = {}) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const { tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [recettes, setRecettes] = useState([]);
  const [repasList, setRepasList] = useState([]);
  const [feuille, setFeuille] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [ajout, setAjout] = useState(null);   // repas dont on remplit les courses
  const [listeCourses, setListeCourses] = useState([]);
  const [erreur, setErreur] = useState('');
  const signaler = (quoi) => (e) => setErreur(`${quoi} — ${e?.message || e?.code || 'erreur'}`);

  useEffect(() => ecouterRecettes(tenantId, (liste) => { setErreur(''); setRecettes(liste); }, signaler('recettes')), [tenantId]);
  useEffect(() => ecouterRepasComposes(tenantId, (liste) => { setErreur(''); setRepasList(liste); }, signaler('repas')), [tenantId]);
  useEffect(() => ecouterListeCourses(tenantId, setListeCourses), [tenantId]);

  const recettesById = useMemo(() => Object.fromEntries(recettes.map(r => [r.id, r])), [recettes]);
  const { emojiPour: emojiIngredient } = useCatalogueIngredients();
  const echec = (e) => Alert.alert(t('repas.erreur'), e?.message || '');

  // La feuille d'ajout renvoie les ingrédients DÉJÀ mis à l'échelle, et
  // éventuellement corrigés à la main : on ne recalcule rien ici.
  const ajouterIngredients = async (ingredients) => {
    try {
      const espaceId = tenantId || await assurerEspace();
      await enregistrerListeCourses(espaceId, fusionnerDansListe(listeCourses, ingredients));
      Alert.alert(t('courses.ajoutTitre'), t('courses.ajoutMsg', { n: ingredients.length }));
    } catch (e) { echec(e); }
  };

  const enregistrer = async (r) => {
    try {
      if (feuille?.initial?.id) await modifierRepas(feuille.initial.id, r);
      else {
        const espaceId = tenantId || await assurerEspace();
        await creerRepas({ ...r, espaceId, creePar: user?.uid, creeParNom: user?.nom });
      }
      setFeuille(null);
    } catch (e) { echec(e); }
  };

  return (
    <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={t('repas.titre')} iconeTitre="🍲" onRetour={onFermer}
        action={{ icone: '＋', label: t('repas.nouveauRepas'), onPress: () => setFeuille({ initial: null }) }} />

      <ScrollView contentContainerStyle={s.corps}>
        {!!erreur && (
          <TouchableOpacity style={s.erreur} activeOpacity={0.8} onPress={() => setErreur('')}>
            <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
          </TouchableOpacity>
        )}

        {repasList.length === 0 ? (
          <EmptyState icon="🍲" texte={t('repas.aucunRepas')} aide={t('repas.aucunRepasAide')} />
        ) : repasList.map(rp => (
          <LigneSwipe key={rp.id} espacement={8} enabled
            onEditer={() => setFeuille({ initial: rp })}
            onDupliquer={() => dupliquerRepas(rp).catch(echec)}
            onSupprimer={() => confirmerSuppression(t('repas.supprimerRepas'), rp.nom,
              () => supprimerRepas(rp.id).catch(echec))}>
            <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setFiche(rp)}>
              <Text style={{ fontSize: 22 }}>{rp.icone || '🍽️'}</Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={s.nom} numberOfLines={1}>{rp.nom}</Text>
                <Text style={s.sous} numberOfLines={1}>
                  {resumeRepas(rp)}  ·  {rp.personnes} {t('repas.personnes')}
                </Text>
              </View>
              {coutRepas(rp, recettesById) > 0 && <Text style={s.cout}>{coutRepas(rp, recettesById).toFixed(2)} €</Text>}
            </TouchableOpacity>
          </LigneSwipe>
        ))}
      </ScrollView>

      <FicheRepas visible={!!fiche} repas={fiche} recettesById={recettesById}
        onFermer={() => setFiche(null)}
        onEditer={() => { setFeuille({ initial: fiche }); setFiche(null); }}
        onSupprimer={() => confirmerSuppression(t('repas.supprimerRepas'), fiche?.nom,
          () => { supprimerRepas(fiche.id).catch(echec); setFiche(null); })}
        onAjouterListe={() => setAjout(fiche)} />

      {/* Feuille d'ajout aux courses : rendue au niveau de l'ÉCRAN, en modale
          SŒUR de la fiche — imbriquer deux <Modal> natives fige l'app. */}
      <FeuilleAjoutCourses visible={!!ajout} titre={ajout?.nom} emoji={ajout?.icone || '🍽️'}
        mode="personnes" personnesInit={ajout ? personnesRepas(ajout) : 4}
        calculer={(n) => (ajout ? ingredientsRepasEchelle(ajout, recettesById, n) : [])}
        emojiPour={emojiIngredient}
        onValider={ajouterIngredients} onFermer={() => setAjout(null)} />

      <FeuilleRepas visible={!!feuille} initial={feuille?.initial} recettes={recettes}
        onEnregistrer={enregistrer} onFermer={() => setFeuille(null)} />
    </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  nom: { fontSize: 15, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 2 },
  cout: { fontSize: 14, fontWeight: '800', color: UI.ink },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
});
